import { Modal } from "antd";
import axios from "axios";
import { Alert, Box, TextField } from "@mui/material";
import React, { createContext, useContext, useMemo, useState } from "react";
import { Text } from "@src/lib/ui-react";

import { useSession } from "@src/appV2/Auth/api/useSession";
import { logoutUserFromEverywhere } from "@src/appV2/Auth/api/logoutUserFromEverywhere";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { environmentConfig } from "@src/appV2/environment";
import { useImpersonationSessions } from "@src/appV2/ImpersonationSessions/ImpersonationSessionsProvider";
import { APP_V2_APP_EVENTS, APP_V3_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { showErrorToast, showInfoToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { useTu } from "@src/appV2/i18n/useTu";
import type { TuFunction } from "@src/appV2/i18n/types";

import { getErrorMessage } from "@src/appV2/api/errors";

import { useMintAdminMagicLink } from "./useMintAdminMagicLink";
import { useResetUserPassword } from "./useResetUserPassword";

const LOGOUT_FROM_ALL_DEVICES = {
  dialogTitle: "Log out from all devices?",
  dialogContent: (userName: string) =>
    `This will log ${userName} out of all devices immediately. Continue?`,
  okText: "Log Out From All Devices",
  userNameFallback: "this user",
  successToast: "User has been logged out of all devices.",
  permissionErrorToast: "You don't have permission to log this user out.",
  notFoundInfoToast: "This user has no Cognito account to log out of.",
  logErrorEvent: "Error in useWorkplaceUserAdminActions - logoutFromAllDevices",
} as const;

function showMintError(err: unknown): void {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const responseData = err.response?.data as
      | { message?: string; retryAfterSeconds?: number }
      | undefined;
    const message = responseData?.message;

    if (status === 404) {
      showErrorToast(
        "No Cognito account for this email yet. Ask them to attempt login once — that'll provision the account, then this link will work."
      );
      return;
    }
    if (status === 422) {
      showErrorToast(message ?? "This user can't be issued a login link right now.");
      return;
    }
    if (status === 429) {
      const retryAfter = responseData?.retryAfterSeconds;
      showErrorToast(
        retryAfter !== undefined
          ? `Too many requests — try again in ${retryAfter} seconds.`
          : (message ?? "Too many requests. Try again shortly.")
      );
      return;
    }
    if (message) {
      showErrorToast(message);
      return;
    }
  }
  if (err instanceof Error) {
    showErrorToast(err.message);
    return;
  }
  showErrorToast("Failed to mint login link.");
}

interface ShowResetPasswordErrorParams {
  error: unknown;
  tu: TuFunction;
}

function showResetPasswordError(params: ShowResetPasswordErrorParams): void {
  const { error, tu } = params;

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      showErrorToast(tu("You don't have permission to set a temporary password."));
      return;
    }
    if (status === 404) {
      showErrorToast(
        tu(
          "No Cognito account exists for this email yet. Ask the user to try signing in once, then try again."
        )
      );
      return;
    }
    if (status === 422) {
      showErrorToast(tu("This account is not eligible for a temporary password."));
      return;
    }
  }

  showErrorToast(getErrorMessage(error, tu("Failed to set temporary password.")));
}

interface CopyTemporaryPasswordToClipboardParams {
  temporaryPassword: string;
  tu: TuFunction;
  showFailureToast?: boolean;
}

async function copyTemporaryPasswordToClipboard(
  params: CopyTemporaryPasswordToClipboardParams
): Promise<boolean> {
  const { temporaryPassword, tu, showFailureToast = false } = params;

  if (typeof navigator === "undefined" || typeof navigator.clipboard?.writeText !== "function") {
    if (showFailureToast) {
      showErrorToast(tu("Unable to copy the temporary password. Please select and copy it."));
    }
    return false;
  }

  try {
    await navigator.clipboard.writeText(temporaryPassword);
    showSuccessToast(tu("Temporary password copied to clipboard"));
    return true;
  } catch {
    if (showFailureToast) {
      showErrorToast(tu("Unable to copy the temporary password. Please select and copy it."));
    }
    return false;
  }
}

export interface WorkplaceUserAdminActionsTarget {
  _id: string;
  name?: string;
  email: string;
}

export interface WorkplaceUserAdminActions {
  canImpersonate: boolean;
  canCopyLink: boolean;
  canSetTemporaryPassword: boolean;
  canLogoutFromEverywhere: boolean;
  impersonate: (user: WorkplaceUserAdminActionsTarget) => void;
  copyMagicLink: (email: string) => void;
  setTemporaryPassword: (user: WorkplaceUserAdminActionsTarget) => void;
  logoutFromEverywhere: (user: WorkplaceUserAdminActionsTarget) => void;
  pendingEmail: string | null;
  pendingTemporaryPasswordEmail: string | null;
}

interface TemporaryPasswordModalState {
  email: string;
  temporaryPassword: string;
}

export function useWorkplaceUserAdminActions(): WorkplaceUserAdminActions & {
  fallbackLink: string | null;
  dismissFallback: () => void;
  temporaryPasswordModal: TemporaryPasswordModalState | null;
  dismissTemporaryPassword: () => void;
  copyTemporaryPassword: () => void;
} {
  const { openSession } = useImpersonationSessions();
  const {
    canImpersonateUsers,
    canGetWorkplaceUserMagicLink,
    canResetWorkplaceUserPassword,
    canLogoutUserFromEverywhere,
  } = useEffectivePermissions();
  const { tu } = useTu("workplaceUsers");
  const session = useSession();
  const mintMagicLink = useMintAdminMagicLink();
  const resetUserPassword = useResetUserPassword();
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pendingTemporaryPasswordEmail, setPendingTemporaryPasswordEmail] = useState<string | null>(
    null
  );
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);
  const [temporaryPasswordModal, setTemporaryPasswordModal] =
    useState<TemporaryPasswordModalState | null>(null);

  const impersonate = (user: WorkplaceUserAdminActionsTarget) => {
    if (!canImpersonateUsers) {
      return;
    }
    openSession(
      "workplace",
      environmentConfig.REACT_APP_WORKPLACE_APP_URL,
      user._id,
      user.name ?? user.email,
      user.email
    );
  };

  // Must run synchronously inside the click handler — awaiting the mint before
  // calling navigator.clipboard.write breaks the Safari user-gesture chain and
  // the write is silently rejected.
  const copyMagicLink = (email: string) => {
    setPendingEmail(email);

    const mintPromise = mintMagicLink.mutateAsync({
      email,
      requesterUrl: `${environmentConfig.REACT_APP_ADMIN_APP_CLIENT_URL}/v2/email-login-link`,
    });

    const supportsDeferredClipboardWrite =
      typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function";
    const writePromise = supportsDeferredClipboardWrite
      ? navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": mintPromise.then(
              ({ loginLink }) => new Blob([loginLink], { type: "text/plain" })
            ),
          }),
        ])
      : undefined;
    writePromise?.catch(() => undefined);

    mintPromise
      .then(async ({ loginLink }) => {
        let copiedToClipboard = false;
        if (writePromise) {
          try {
            await writePromise;
            copiedToClipboard = true;
            showSuccessToast("Login link copied to clipboard");
          } catch {
            // fall through to the visible-link modal
          }
        }
        logEvent(APP_V2_APP_EVENTS.MINT_ADMIN_MAGIC_LINK_SUCCESS, {
          adminUserId: session?.adminProfile?.userId,
          adminEmail: session?.adminProfile?.email,
          targetEmail: email,
          copiedToClipboard,
        });
        if (!copiedToClipboard) {
          setFallbackLink(loginLink);
        }
      })
      .catch(showMintError)
      .finally(() => setPendingEmail(null));
  };

  const setTemporaryPassword = (user: WorkplaceUserAdminActionsTarget) => {
    if (!canResetWorkplaceUserPassword) {
      return;
    }

    setPendingTemporaryPasswordEmail(user.email);

    resetUserPassword
      .mutateAsync({ email: user.email })
      .then(async ({ temporaryPassword }) => {
        setTemporaryPasswordModal({ email: user.email, temporaryPassword });
        const copiedToClipboard = await copyTemporaryPasswordToClipboard({ temporaryPassword, tu });

        logEvent(APP_V3_APP_EVENTS.RESET_USER_PASSWORD_SUCCESS, {
          adminUserId: session?.adminProfile?.userId,
          adminEmail: session?.adminProfile?.email,
          targetEmail: user.email,
          copiedToClipboard,
        });
      })
      .catch((error: unknown) => showResetPasswordError({ error, tu }))
      .finally(() => setPendingTemporaryPasswordEmail(null));
  };

  const logoutFromEverywhere = (user: WorkplaceUserAdminActionsTarget) => {
    if (!canLogoutUserFromEverywhere) {
      return;
    }
    const userName = user.name ?? user.email ?? LOGOUT_FROM_ALL_DEVICES.userNameFallback;

    Modal.confirm({
      title: LOGOUT_FROM_ALL_DEVICES.dialogTitle,
      content: LOGOUT_FROM_ALL_DEVICES.dialogContent(userName),
      okText: LOGOUT_FROM_ALL_DEVICES.okText,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await logoutUserFromEverywhere({ userId: user._id });
          showSuccessToast(LOGOUT_FROM_ALL_DEVICES.successToast);
        } catch (error: unknown) {
          if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            if (status === 401 || status === 403) {
              showErrorToast(LOGOUT_FROM_ALL_DEVICES.permissionErrorToast);
              return;
            }
            if (status === 404) {
              showInfoToast(LOGOUT_FROM_ALL_DEVICES.notFoundInfoToast);
              return;
            }
          }
          logError(LOGOUT_FROM_ALL_DEVICES.logErrorEvent, { error });
          showErrorToast(getErrorMessage(error));
        }
      },
    });
  };

  const dismissTemporaryPassword = () => {
    setTemporaryPasswordModal(null);
  };

  const copyTemporaryPassword = () => {
    if (temporaryPasswordModal === null) {
      return;
    }

    void copyTemporaryPasswordToClipboard({
      temporaryPassword: temporaryPasswordModal.temporaryPassword,
      tu,
      showFailureToast: true,
    });
  };

  return {
    canImpersonate: canImpersonateUsers,
    canCopyLink: canGetWorkplaceUserMagicLink,
    canSetTemporaryPassword: canResetWorkplaceUserPassword,
    canLogoutFromEverywhere: canLogoutUserFromEverywhere,
    impersonate,
    copyMagicLink,
    setTemporaryPassword,
    logoutFromEverywhere,
    pendingEmail,
    pendingTemporaryPasswordEmail,
    fallbackLink,
    dismissFallback: () => setFallbackLink(null),
    temporaryPasswordModal,
    dismissTemporaryPassword,
    copyTemporaryPassword,
  };
}

const WorkplaceUserAdminActionsContext = createContext<WorkplaceUserAdminActions | null>(null);

export function useWorkplaceUserAdminActionsContext(): WorkplaceUserAdminActions | null {
  return useContext(WorkplaceUserAdminActionsContext);
}

interface WorkplaceUserAdminActionsProviderProps {
  children: React.ReactNode;
}

export function WorkplaceUserAdminActionsProvider(props: WorkplaceUserAdminActionsProviderProps) {
  const { children } = props;
  const {
    canImpersonate,
    canCopyLink,
    canSetTemporaryPassword,
    canLogoutFromEverywhere,
    impersonate,
    copyMagicLink,
    setTemporaryPassword,
    logoutFromEverywhere,
    pendingEmail,
    pendingTemporaryPasswordEmail,
    fallbackLink,
    dismissFallback,
    temporaryPasswordModal,
    dismissTemporaryPassword,
    copyTemporaryPassword,
  } = useWorkplaceUserAdminActions();
  const { tu } = useTu("workplaceUsers");

  const value = useMemo<WorkplaceUserAdminActions>(
    () => ({
      canImpersonate,
      canCopyLink,
      canSetTemporaryPassword,
      canLogoutFromEverywhere,
      impersonate,
      copyMagicLink,
      setTemporaryPassword,
      logoutFromEverywhere,
      pendingEmail,
      pendingTemporaryPasswordEmail,
    }),
    [
      canImpersonate,
      canCopyLink,
      canSetTemporaryPassword,
      canLogoutFromEverywhere,
      impersonate,
      copyMagicLink,
      setTemporaryPassword,
      logoutFromEverywhere,
      pendingEmail,
      pendingTemporaryPasswordEmail,
    ]
  );

  return (
    <WorkplaceUserAdminActionsContext.Provider value={value}>
      {children}
      <Modal
        title="Oops, unable to copy link to clipboard"
        visible={fallbackLink !== null}
        onCancel={dismissFallback}
        footer={null}
      >
        <p style={{ marginBottom: "10px" }}>Please copy the following link:</p>
        <p>{fallbackLink}</p>
      </Modal>
      <Modal
        title={tu("Temporary password created")}
        visible={temporaryPasswordModal !== null}
        destroyOnClose={true}
        onOk={copyTemporaryPassword}
        okText={tu("Copy temporary password")}
        onCancel={dismissTemporaryPassword}
        cancelText={tu("Done")}
      >
        {temporaryPasswordModal !== null ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="warning">
              {tu(
                "This temporary password is shown only once. The user must change it the next time they sign in."
              )}
            </Alert>
            <Text variant="body2">
              {tu("Share this password with {{email}}, then close this dialog.", {
                values: { email: temporaryPasswordModal.email },
              })}
            </Text>
            <TextField
              label={tu("Temporary password")}
              value={temporaryPasswordModal.temporaryPassword}
              fullWidth
              autoComplete="off"
              data-dd-privacy="mask"
              InputProps={{ readOnly: true }}
              inputProps={{ "data-dd-privacy": "mask", autoComplete: "off" }}
            />
          </Box>
        ) : null}
      </Modal>
    </WorkplaceUserAdminActionsContext.Provider>
  );
}
